import {
  Button,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Table,
  TableColumns,
  useDefaultSorting,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SortDirection, usePimDeleteUserMutation, usePimListUsersQuery } from '../../client/graphql-client-defs';
import { getTokens } from '../../utils/authStore';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { ListUsersType } from './graphql/types';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const Users = () => {
  const { t } = useTranslation();

  const { sorting, onSortingChange } = useDefaultSorting();
  const [, deleteUser] = usePimDeleteUserMutation();

  const context = useMemo(() => ({ additionalTypenames: ['Organization'] }), []);

  const [{ data }, refetchUsers] = usePimListUsersQuery({
    variables: {
      options: {
        sort: [{ emailLower: SortDirection.Asc }],
      },
    },
    context,
  });

  async function resendInvite(profileUuid: string, email: string, frontend: 'planung' | 'bp' | 'zeugnis') {
    const tokens = getTokens();
    await fetch(`${import.meta.env.VITE_APP_AUTH_SERVER_URI}/invitation/${profileUuid}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokens?.access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        profileUuid: profileUuid,
        product: 'planung',
        baseUrl: frontend === 'planung' ? 'https://planung.tks.eu' : 'https://go.bildungsplattform.org',
        email: email,
      }),
    });
  }

  async function handleDeleteUser(row: ListUsersType) {
    const result = await deleteUser({ where: { uuid: row.uuid } });
    if (result.error) {
      showErrorToast(result.error);
    } else {
      showSuccessToast();
      refetchUsers({ requestPolicy: 'network-only' });
    }
  }

  async function pwReset(email: string) {
    await fetch(`${import.meta.env.VITE_APP_AUTH_SERVER_URI}/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        email: email,
      }),
    });
  }

  const tableColumns: TableColumns<ListUsersType>[] = useMemo(() => {
    return [
      {
        header: 'Uuid',
        id: 'uuid',
        accessorKey: 'uuid',
        size: 150,
      },
      {
        header: 'Email',
        id: 'emailLower',
        accessorKey: 'emailLower',
        size: 250,
        canExpand: true,
      },
      {
        header: 'Email',
        id: 'emailVerified',
        accessorKey: 'emailVerified',
        size: 50,
      },
      {
        header: 'Invitation',
        id: 'invitation',
        accessorKey: 'invitationOtpValidUntil',
        size: 250,
        canExpand: true,
      },
      {
        header: 'PW-Reset',
        id: 'pw-reset',
        accessorKey: 'resetPasswordOtpValidUntil',
        size: 250,
        canExpand: true,
      },
      {
        header: 'Status',
        id: 'status',
        accessorKey: 'status',
        accessorFn: (row) => row.status,
        size: 150,
      },
    ];
  }, []);

  const tableData = useMemo((): ListUsersType[] => {
    return data && data.users
      ? data.users.map((u) => {
          return {
            ...u,
            status: u.impersonateOnly ? 'CHILD' : u.invitationOtpValidUntil ? 'INVITED' : 'ACTIVE',
          };
        })
      : [];
  }, [data]);

  return (
    <div className='page'>
      <h2>{t('users.title')}</h2>
      <Table<ListUsersType>
        showSelect={false}
        showActionBar
        showSort
        sorting={sorting}
        onSortingChange={onSortingChange}
        isOnWhite={false}
        columns={tableColumns}
        data={tableData}
        lastCol={(row) => {
          return (
            <Dropdown
              trigger={
                <Button isLoading={false} hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />
              }
            >
              <DropdownMenu
                data={[
                  {
                    label: 'Löschen',
                    disabled: row.original.profiles.length > 0,
                    onClick: () => handleDeleteUser(row.original),
                  },
                  ...((row.original.profiles?.flatMap((p) => {
                    return [
                      {
                        label: p.listName + ' (' + p.organization.name + ') Einladung (TK-Planung)',
                        disabled: row.original.status !== 'INVITED',
                        onClick: () => resendInvite(p.uuid, row.original.email, 'planung'),
                      },
                      {
                        label: p.listName + ' (' + p.organization.name + ') Einladung (BP)',
                        disabled: row.original.status !== 'INVITED',
                        onClick: () => resendInvite(p.uuid, row.original.email, 'bp'),
                      },
                      {
                        type: 'ruler',
                      },
                      {
                        label: 'PW vergessen verschicken',
                        disabled: row.original.status !== 'ACTIVE',
                        onClick: () => pwReset(row.original.uuid),
                      },
                    ];
                  }) ?? []) as DropdownMenuItem[]),
                ]}
              ></DropdownMenu>
            </Dropdown>
          );
        }}
        lastColWidth={'100px'}
      />
    </div>
  );
};
